// src/components/ecommerce/utils/storeTypeConfig.js
import { 
  Package, 
  Users, 
  Briefcase, 
  Utensils, 
  Home,
  Shirt,
  Palette,
  Heart,
  Book,
  Zap,
  Globe,
  ShoppingBag
} from 'lucide-react';

export const STORE_TYPES = {
  general: {
    id: 'general',
    label: 'General Store',
    icon: ShoppingBag,
    itemName: 'Product',
    itemNamePlural: 'Products',
    addItemLabel: 'Add Product',
    emptyMessage: 'No products yet',
    emptyMessageDetail: 'Start by adding your first product to your store',
    supportedFeatures: ['cart', 'wishlist', 'reviews', 'inventory', 'variants'],
    color: '#3b82f6'
  },
  services: {
    id: 'services',
    label: 'Services',
    icon: Users,
    itemName: 'Service',
    itemNamePlural: 'Services',
    addItemLabel: 'Add Service',
    emptyMessage: 'No services available',
    emptyMessageDetail: 'Create your first service offering',
    supportedFeatures: ['bookings', 'reviews', 'calendar'],
    color: '#8b5cf6',
    orderLabel: 'Booking',
    orderLabelPlural: 'Bookings',
    customerLabel: 'Client',
    customerLabelPlural: 'Clients'
  },
  digital: {
    id: 'digital',
    label: 'Digital Products',
    icon: Zap,
    itemName: 'Digital Product',
    itemNamePlural: 'Digital Products',
    addItemLabel: 'Add Digital Product',
    emptyMessage: 'No digital products yet',
    emptyMessageDetail: 'Add downloadable products like ebooks, software, or courses',
    supportedFeatures: ['cart', 'downloads', 'licenses'],
    color: '#f59e0b'
  },
  fashion: {
    id: 'fashion',
    label: 'Fashion Store',
    icon: Shirt,
    itemName: 'Product',
    itemNamePlural: 'Products',
    addItemLabel: 'Add Product',
    emptyMessage: 'No products yet',
    emptyMessageDetail: 'Add clothing, accessories, or fashion items',
    supportedFeatures: ['cart', 'wishlist', 'reviews', 'variants', 'sizeGuide'],
    color: '#ec4899'
  },
  food: {
    id: 'food',
    label: 'Restaurant/Food',
    icon: Utensils,
    itemName: 'Menu Item',
    itemNamePlural: 'Menu Items',
    addItemLabel: 'Add Menu Item',
    emptyMessage: 'No menu items yet',
    emptyMessageDetail: 'Add dishes, drinks, or food items to your menu',
    supportedFeatures: ['cart', 'delivery', 'pickup', 'variants'],
    color: '#ef4444',
    orderLabel: 'Order',
    orderLabelPlural: 'Orders'
  },
  consulting: {
    id: 'consulting',
    label: 'Consulting',
    icon: Briefcase,
    itemName: 'Consulting Service',
    itemNamePlural: 'Consulting Services',
    addItemLabel: 'Add Service',
    emptyMessage: 'No consulting services yet',
    emptyMessageDetail: 'Define your consulting packages and offerings',
    supportedFeatures: ['bookings', 'calendar', 'proposals'],
    color: '#0ea5e9',
    orderLabel: 'Engagement',
    orderLabelPlural: 'Engagements',
    customerLabel: 'Client',
    customerLabelPlural: 'Clients'
  },
  rentals: {
    id: 'rentals',
    label: 'Rentals',
    icon: Home,
    itemName: 'Rental',
    itemNamePlural: 'Rentals',
    addItemLabel: 'Add Rental',
    emptyMessage: 'No rentals available',
    emptyMessageDetail: 'List properties, equipment, or items for rent',
    supportedFeatures: ['bookings', 'calendar', 'availability'],
    color: '#10b981',
    orderLabel: 'Booking',
    orderLabelPlural: 'Bookings'
  },
  creative: {
    id: 'creative',
    label: 'Creative Services',
    icon: Palette,
    itemName: 'Service',
    itemNamePlural: 'Services',
    addItemLabel: 'Add Service',
    emptyMessage: 'No services available',
    emptyMessageDetail: 'Offer design, photography, or creative services',
    supportedFeatures: ['bookings', 'portfolio', 'quotes'],
    color: '#a855f7',
    orderLabel: 'Project',
    orderLabelPlural: 'Projects',
    customerLabel: 'Client',
    customerLabelPlural: 'Clients'
  },
  wellness: {
    id: 'wellness',
    label: 'Wellness/Spa',
    icon: Heart,
    itemName: 'Treatment',
    itemNamePlural: 'Treatments',
    addItemLabel: 'Add Treatment',
    emptyMessage: 'No treatments available',
    emptyMessageDetail: 'Add spa treatments, therapies, or wellness services',
    supportedFeatures: ['bookings', 'packages', 'memberships'],
    color: '#ec4899',
    orderLabel: 'Appointment',
    orderLabelPlural: 'Appointments',
    customerLabel: 'Client',
    customerLabelPlural: 'Clients'
  },
  education: {
    id: 'education',
    label: 'Education/Courses',
    icon: Book,
    itemName: 'Course',
    itemNamePlural: 'Courses',
    addItemLabel: 'Add Course',
    emptyMessage: 'No courses available',
    emptyMessageDetail: 'Create online courses or educational content',
    supportedFeatures: ['enrollments', 'certificates', 'progress'],
    color: '#6366f1',
    orderLabel: 'Enrollment',
    orderLabelPlural: 'Enrollments',
    customerLabel: 'Student',
    customerLabelPlural: 'Students'
  },
  marketplace: {
    id: 'marketplace',
    label: 'Marketplace',
    icon: Globe,
    itemName: 'Product',
    itemNamePlural: 'Products',
    addItemLabel: 'Add Product',
    emptyMessage: 'No products yet',
    emptyMessageDetail: 'Multi-vendor marketplace platform',
    supportedFeatures: ['cart', 'wishlist', 'reviews', 'multiVendor'],
    color: '#06b6d4',
    orderLabel: 'Order',
    orderLabelPlural: 'Orders',
    customerLabel: 'Buyer',
    customerLabelPlural: 'Buyers'
  }
};

/**
 * Get store type configuration
 * @param {string} storeType - The store type ID
 * @returns {object} Store type configuration
 */
export const getStoreTypeConfig = (storeType) => {
  return STORE_TYPES[storeType] || STORE_TYPES.general;
};

/**
 * Get all available store types
 * @returns {array} Array of store type configurations
 */
export const getAllStoreTypes = () => {
  return Object.values(STORE_TYPES);
};

/**
 * Check if a feature is supported by store type
 * @param {string} storeType - The store type ID
 * @param {string} feature - The feature to check
 * @returns {boolean} Whether the feature is supported
 */
export const isFeatureSupported = (storeType, feature) => {
  const config = getStoreTypeConfig(storeType);
  return config.supportedFeatures.includes(feature);
};